import { Check, Copy, ExternalLink, Loader2, Mail, MessageCircle, Share2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { getDocumentosPublicoControllerPdfUrl } from '@/api/generated/documentos/documentos';
import type { LinkCompartilhadoResponse } from '@/api/generated/model';
import { useTutoresControllerAtualizar } from '@/api/generated/tutores/tutores';
import { ApiError, baixarArquivo } from '@/api/http-client';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { CampoDeTelefone } from '@/shared/components/CampoDeTelefone';
import { linkDoWhatsapp, telefoneValido } from '@/shared/lib/telefone';
import { usePermissoes } from '@/shared/permissoes/usePermissoes';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { toast } from '@/shared/ui/sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';

export type TipoDeDocumento = 'RECEITUARIO' | 'REQUISICAO';

const ROTULO_DO_TIPO: Record<TipoDeDocumento, string> = {
  RECEITUARIO: 'receituário',
  REQUISICAO: 'requisição de exames',
};

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  link: LinkCompartilhadoResponse;
  tipo: TipoDeDocumento;
  pacienteNome: string;
  veterinarioNome: string;
  /**
   * Envia por e-mail e devolve o link NOVO.
   *
   * Vem de fora porque a rota é do módulo dono (`/receituarios/:id/...` ou
   * `/requisicoes-exames/:id/...`) — é o que mantém este diálogo servindo aos
   * dois documentos sem conhecer nenhum dos dois.
   */
  aoEnviarPorEmail: (dados: {
    email: string;
    salvarNoTutor: boolean;
  }) => Promise<LinkCompartilhadoResponse>;
}

/** Qual campo está esperando a confirmação de "salvar no cadastro?". */
type AConfirmar = 'email' | 'whatsapp' | null;

/**
 * Entrega do documento ao tutor: link, e-mail ou WhatsApp.
 *
 * Fica em `shared/` porque requisição e receituário compartilham o mesmo
 * contrato e a mesma regra de negócio. Duplicar daria duas telas para divergir.
 *
 * ⚠️ **A validade fica visível de propósito.** Na V1 o PDF compartilhado ia
 * para um bucket público e o link valia para sempre; aqui ele expira e cada
 * acesso é registrado. Quem manda o link para o tutor precisa saber até quando
 * ele funciona — senão promete um acesso que vai sumir.
 *
 * ⚠️ **O WhatsApp abre uma conversa com TEXTO, não com anexo.** O `wa.me` não
 * carrega arquivo; a mensagem leva o link. Onde o aparelho suporta
 * (`navigator.share` com arquivos, na prática celular), aparece um botão a mais
 * que compartilha o PDF de verdade.
 */
export function CompartilharDialogo({
  aberto,
  aoFechar,
  link,
  tipo,
  pacienteNome,
  veterinarioNome,
  aoEnviarPorEmail,
}: Props) {
  const { pode } = usePermissoes();
  const atualizarTutor = useTutoresControllerAtualizar();

  // O envio por e-mail gera um link novo (com validade própria), e é ele que
  // passa a valer na aba Link — senão a tela mostraria um endereço diferente do
  // que acabou de sair na caixa do tutor.
  const [atual, setAtual] = useState(link);
  useEffect(() => setAtual(link), [link]);

  const destinatario = atual.destinatario;

  const [copiado, setCopiado] = useState(false);
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [aConfirmar, setAConfirmar] = useState<AConfirmar>(null);
  const [compartilhandoArquivo, setCompartilhandoArquivo] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    setEmail(destinatario?.email ?? '');
    setTelefone(destinatario?.telefone ?? '');
    setAConfirmar(null);
  }, [aberto, destinatario]);

  const podeSalvarNoCadastro = Boolean(destinatario) && pode('tutores.editar');
  const validade = new Date(atual.expiraEm).toLocaleString('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  });

  const mensagem = [
    `Olá! Segue o ${ROTULO_DO_TIPO[tipo]} de ${pacienteNome}, emitido por ${veterinarioNome}.`,
    '',
    `Abrir: ${atual.url}`,
    `O link vale até ${validade}.`,
  ].join('\n');

  async function copiar() {
    try {
      await navigator.clipboard.writeText(atual.url);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      // Clipboard bloqueado (permissão negada, contexto não seguro): o campo
      // continua selecionável, então o caminho manual segue aberto.
      toast.error('Não foi possível copiar. Selecione o link e copie à mão.');
    }
  }

  /**
   * O passo de confirmação, comum ao e-mail e ao WhatsApp.
   *
   * Só pergunta quando o valor DIVERGE do cadastro — mandar para o endereço que
   * já está gravado não é uma alteração e não merece um diálogo no caminho.
   * Cadastro vazio também diverge: aí a pergunta é "quer aproveitar e salvar?".
   */
  function confirmarOuSeguir(qual: Exclude<AConfirmar, null>, valor: string, gravado: string | null) {
    if (podeSalvarNoCadastro && valor !== (gravado ?? '')) {
      setAConfirmar(qual);
      return;
    }

    void (qual === 'email' ? enviarEmail(false) : abrirWhatsapp(false));
  }

  async function enviarEmail(salvarNoTutor: boolean) {
    setAConfirmar(null);
    setEnviando(true);

    try {
      setAtual(await aoEnviarPorEmail({ email, salvarNoTutor }));
      toast.success(
        salvarNoTutor ? 'E-mail enviado e cadastro atualizado.' : `E-mail enviado para ${email}.`,
      );
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível enviar o e-mail.');
    } finally {
      setEnviando(false);
    }
  }

  async function abrirWhatsapp(salvarNoTutor: boolean) {
    setAConfirmar(null);

    if (salvarNoTutor && destinatario) {
      try {
        await atualizarTutor.mutateAsync({ id: destinatario.tutorId, data: { telefone } });
        toast.success('Telefone atualizado no cadastro.');
      } catch (erro) {
        // O cadastro falhou, mas a mensagem ainda pode ir — abrir o WhatsApp
        // mesmo assim evita transformar um erro de gravação em "não consegui
        // mandar a receita".
        toast.error(
          erro instanceof ApiError ? erro.message : 'Não foi possível salvar o telefone.',
        );
      }
    }

    window.open(linkDoWhatsapp(telefone, mensagem), '_blank', 'noopener,noreferrer');
  }

  /**
   * Compartilhamento nativo do PDF — o "com o arquivo" que o `wa.me` não faz.
   *
   * Só existe onde o aparelho aceita compartilhar arquivo (na prática, celular).
   * Em desktop `canShare` devolve falso e o botão nem aparece, em vez de abrir
   * um seletor que não leva a lugar nenhum.
   */
  async function compartilharArquivo() {
    setCompartilhandoArquivo(true);
    try {
      const conteudo = await baixarArquivo(getDocumentosPublicoControllerPdfUrl(atual.token));

      const arquivo = new File([conteudo], `${ROTULO_DO_TIPO[tipo]}.pdf`, {
        type: 'application/pdf',
      });

      await navigator.share({ files: [arquivo], text: mensagem });
    } catch (erro) {
      // Cancelar o seletor do sistema rejeita com AbortError — é desistência,
      // não falha, e avisar seria acusar o usuário de um erro que não houve.
      if (erro instanceof DOMException && erro.name === 'AbortError') return;
      toast.error('Não foi possível compartilhar o arquivo. Use o link.');
    } finally {
      setCompartilhandoArquivo(false);
    }
  }

  const podeCompartilharArquivo =
    typeof navigator !== 'undefined' && typeof navigator.canShare === 'function'
      ? navigator.canShare({ files: [new File([], 'x.pdf', { type: 'application/pdf' })] })
      : false;

  return (
    <>
      <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Compartilhar documento</DialogTitle>
            <DialogDescription>
              Qualquer pessoa com o link vê o documento. Cada acesso fica registrado.
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="link">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="link">
                <Copy className="w-4 h-4 mr-2" />
                Link
              </TabsTrigger>
              <TabsTrigger value="email">
                <Mail className="w-4 h-4 mr-2" />
                E-mail
              </TabsTrigger>
              <TabsTrigger value="whatsapp">
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </TabsTrigger>
            </TabsList>

            <TabsContent value="link" className="space-y-3 pt-4">
              <div className="flex gap-2">
                <Input readOnly value={atual.url} aria-label="Link de compartilhamento" />
                <Button variant="outline" onClick={() => void copiar()} aria-label="Copiar link">
                  {copiado ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
                <Button variant="outline" asChild aria-label="Abrir link">
                  <a href={atual.url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="email" className="space-y-3 pt-4">
              <Campo
                id="compartilhar-email"
                rotulo="Enviar para"
                dica={
                  destinatario
                    ? `Responsável: ${destinatario.nome}. O PDF vai anexado.`
                    : 'Este paciente não tem responsável cadastrado. O PDF vai anexado.'
                }
              >
                <Input
                  id="compartilhar-email"
                  type="email"
                  value={email}
                  onChange={(evento) => setEmail(evento.target.value)}
                  placeholder="tutor@exemplo.com"
                  className={CLASSE_CONTROLE}
                />
              </Campo>

              <Button
                className="w-full"
                disabled={enviando || !email.includes('@')}
                onClick={() => confirmarOuSeguir('email', email, destinatario?.email ?? null)}
              >
                {enviando ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Mail className="w-4 h-4 mr-2" />
                )}
                Enviar por e-mail
              </Button>
            </TabsContent>

            <TabsContent value="whatsapp" className="space-y-3 pt-4">
              <Campo
                id="compartilhar-telefone"
                rotulo="Enviar para"
                dica={
                  destinatario
                    ? `Responsável: ${destinatario.nome}.`
                    : 'Este paciente não tem responsável cadastrado.'
                }
              >
                <CampoDeTelefone
                  id="compartilhar-telefone"
                  valor={telefone}
                  aoMudar={setTelefone}
                  aria-label="Telefone do WhatsApp"
                />
              </Campo>

              <Button
                className="w-full"
                disabled={!telefoneValido(telefone)}
                onClick={() =>
                  confirmarOuSeguir('whatsapp', telefone, destinatario?.telefone ?? null)
                }
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Abrir no WhatsApp
              </Button>

              {podeCompartilharArquivo && (
                <Button
                  variant="outline"
                  className="w-full"
                  disabled={compartilhandoArquivo}
                  onClick={() => void compartilharArquivo()}
                >
                  {compartilhandoArquivo ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Share2 className="w-4 h-4 mr-2" />
                  )}
                  Compartilhar o arquivo
                </Button>
              )}

              <p className="text-xs text-muted-foreground">
                A mensagem leva o link do documento. Para mandar o PDF em si, use “Compartilhar o
                arquivo” — disponível no celular.
              </p>
            </TabsContent>
          </Tabs>

          <p className="text-sm text-muted-foreground">{`Válido até ${validade}.`}</p>

          <DialogFooter>
            <Button variant="outline" onClick={aoFechar}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* A confirmação de "permanente ou só neste envio?". Fora do `<Dialog>`
          porque um AlertDialog aninhado dentro do conteúdo do outro herda o
          gerenciamento de foco dele e abre por baixo. */}
      <AlertDialog open={aConfirmar !== null} onOpenChange={(v) => !v && setAConfirmar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {aConfirmar === 'email'
                ? `Salvar este e-mail no cadastro de ${destinatario?.nome}?`
                : `Salvar este telefone no cadastro de ${destinatario?.nome}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {destinatario?.[aConfirmar === 'email' ? 'email' : 'telefone']
                ? 'O contato atual do responsável será substituído. Você também pode usar o novo só desta vez.'
                : 'Este responsável ainda não tem este contato cadastrado. Você pode salvá-lo agora ou usá-lo só desta vez.'}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            {/* "Só desta vez" é `Button`, não `AlertDialogAction`: o Action
                fecha o diálogo por conta própria e o handler correria junto com
                o desmonte. */}
            <Button
              variant="outline"
              onClick={() =>
                void (aConfirmar === 'email' ? enviarEmail(false) : abrirWhatsapp(false))
              }
            >
              Só desta vez
            </Button>
            <AlertDialogAction
              onClick={() =>
                void (aConfirmar === 'email' ? enviarEmail(true) : abrirWhatsapp(true))
              }
            >
              Salvar no cadastro
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
